import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faIndustry,
  faRobot,
  faGlobe,
  faShieldAlt,
  faBrain,
  faCogs,
  faPlug,
  faBolt,
  faBell,
  faFileAlt,
  faChartLine,
  faWrench,
  faClone,
  faClipboardCheck,
  faCheck,
  faArrowRight,
  faQuoteRight,
  faSun,
  faCookieBite,
  faPills,
  faCar,
  faTint,
} from '@fortawesome/free-solid-svg-icons';

import previewHero from '../../../assets/img/Product/AiStudioPreview/praiview_designer.png';
import aiMlIllustration from '../../../assets/img/Product/AiStudioPreview/ai_ml_illustration.svg';
import praiViewLogo from '../../../assets/img/Product/AiStudioPreview/praiview_logo.png';
import designerWorkspace from '../../../assets/img/Product/AiStudioPreview/ourdesigner.png';
import designerIntro from '../../../assets/img/Product/AiStudioPreview/ourdesigner1.png';
import componentConfig from '../../../assets/img/Product/AiStudioPreview/ourdesigner2.png';
import runtimeMenu from '../../../assets/img/Product/AiStudioPreview/runtime_menu.png';
import runtimeDashboard from '../../../assets/img/Product/AiStudioPreview/runtime-dashboard.png';

const PreviewPage = () => {
  const navigate = useNavigate();
  const handleQuote = () => navigate('/quote');

  const pageTitle =
    'prai.view | Industrial AI Dashboard Builder & Runtime - GUI View';
  const pageDescription =
    'prai.view by GUIVIEW is an Industrial AI Dashboard Builder & Runtime Platform. Build SCADA/MES/EMS applications with AI-assisted design, local ML inference, and secure browser-based runtime.';
  const pageKeywords =
    'prai.view, AI Studio, industrial dashboard builder, SCADA, MES, EMS, AI-assisted design, local ML, TensorFlow, predictive maintenance, anomaly detection, browser runtime, GUIVIEW';

  const features = [
    {
      icon: faRobot,
      title: 'Agent-First Design',
      text: 'Spawn Industrial Insight Agents that plan, generate, validate, and document screens, schemas, reports, and MQTT mappings from natural language prompts.',
    },
    {
      icon: faGlobe,
      title: 'Browser-Based Runtime',
      text: 'Deploy projects as a runtime executable. Operators access dashboards, alarms, reports, and AI chat from any modern browser on desktop or tablet.',
    },
    {
      icon: faShieldAlt,
      title: 'Read-Only SCADA Safety',
      text: 'prai.view connects to GUI.Builder and standard MQTT brokers as a client only. It subscribes to live tag data and never publishes control commands back to SCADA.',
    },
    {
      icon: faBrain,
      title: 'Local AI & ML Inference',
      text: 'Run TensorFlow models on-premise for predictive maintenance, anomaly detection, and forecasting — keeping sensitive process data inside your network.',
    },
    {
      icon: faCogs,
      title: 'MES & EMS Modules',
      text: 'Built-in production tracking, OEE, downtime, shift management, energy balance, Sankey flow, carbon footprint, SEC, and demand monitoring.',
    },
    {
      icon: faPlug,
      title: 'Multi-Source Data Connectivity',
      text: 'Connect to MySQL, MariaDB, PostgreSQL, SQL Server, and ODBC sources. Browse schemas, preview data, and bind query results directly to components.',
    },
  ];

  const architecture = [
    {
      title: 'prai.view Designer',
      text: 'Tauri desktop app for licensed integrators. Design screens, configure data sources, orchestrate agents, review artifacts, and package deployments.',
      color: '#6f55ff',
      svg: (
        <svg
          viewBox="0 0 100 100"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <rect
            x="10"
            y="15"
            width="80"
            height="70"
            rx="10"
            fill="#f3f4f6"
            stroke="#6f55ff"
            strokeWidth="2"
          />
          <rect
            x="22"
            y="28"
            width="56"
            height="34"
            rx="4"
            fill="#fff"
            stroke="#d1d5db"
            strokeWidth="1.5"
          />
          <path
            d="M28 42h20M28 50h36"
            stroke="#6f55ff"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <rect x="30" y="70" width="40" height="8" rx="2" fill="#6f55ff" />
        </svg>
      ),
    },
    {
      title: 'Node.js Server',
      text: 'NestJS backend provides REST APIs, WebSocket gateways, authentication, RBAC, AI routing, MQTT client, scheduler, and report engine.',
      color: '#ff6b00',
      svg: (
        <svg
          viewBox="0 0 100 100"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <rect
            x="15"
            y="25"
            width="70"
            height="50"
            rx="8"
            fill="#f3f4f6"
            stroke="#ff6b00"
            strokeWidth="2"
          />
          <circle
            cx="50"
            cy="42"
            r="10"
            fill="#fff"
            stroke="#ff6b00"
            strokeWidth="2"
          />
          <path
            d="M50 38v8l5 3"
            stroke="#ff6b00"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <rect x="25" y="62" width="50" height="6" rx="3" fill="#ff6b00" />
        </svg>
      ),
    },
    {
      title: 'prai.view Runtime',
      text: 'Browser-based React app for operators. Execute dashboards, alarms, reports, MES/EMS modules, and AI chat with role-based access.',
      color: '#3bb0d7',
      svg: (
        <svg
          viewBox="0 0 100 100"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <rect
            x="18"
            y="20"
            width="64"
            height="60"
            rx="10"
            fill="#f3f4f6"
            stroke="#3bb0d7"
            strokeWidth="2"
          />
          <rect
            x="28"
            y="32"
            width="44"
            height="36"
            rx="4"
            fill="#fff"
            stroke="#d1d5db"
            strokeWidth="1.5"
          />
          <path
            d="M34 50h14M34 56h22"
            stroke="#3bb0d7"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <circle cx="70" cy="32" r="8" fill="#3bb0d7" />
        </svg>
      ),
    },
  ];

  const aiCapabilities = [
    {
      title: 'Predictive Maintenance',
      text: 'Estimate failure risk and remaining useful life for motors, pumps, bearings, and more.',
    },
    {
      title: 'Anomaly Detection',
      text: 'LSTM autoencoder continuously monitors live MQTT tags and raises alarms on deviation.',
    },
    {
      title: 'Forecasting',
      text: 'Predict future tag values over configurable horizons using sequence-to-sequence models.',
    },
    {
      title: 'AI Chat - Agent',
      text: 'Seamlessly interact with multiple AI models through a unified chat experience. Automate tasks and boost productivity with advanced AI assistance.',
    },
  ];

  const modules = [
    {
      icon: faIndustry,
      title: 'Production & OEE',
      text: 'Track throughput, downtime, and overall equipment effectiveness in real time.',
    },
    {
      icon: faBolt,
      title: 'Energy Management',
      text: 'Monitor electricity, water, gas, steam, and compressed air with Sankey flows.',
    },
    {
      icon: faBell,
      title: 'Alarms & Events',
      text: 'Alarm banner, acknowledgment, notification center, and full audit trail.',
    },
    {
      icon: faFileAlt,
      title: 'Reports & Scheduler',
      text: 'PDF, Excel, and email reports triggered manually or on schedule.',
    },
    {
      icon: faChartLine,
      title: 'SPC & Quality',
      text: 'Statistical process control, quality analytics, and electronic logbooks.',
    },
    {
      icon: faWrench,
      title: 'Asset / CMMS',
      text: 'Asset registry, runtime counters, maintenance events, and work orders.',
    },
    {
      icon: faClone,
      title: 'Digital Twin',
      text: 'Visualize asset state, historical behavior, and predicted future conditions.',
    },
    {
      icon: faClipboardCheck,
      title: 'Andon & Workflow',
      text: 'Visual call-for-help systems, checklists, and configurable workflows.',
    },
  ];

  const showcase = [
    {
      img: designerWorkspace,
      title: 'Designer Workspace',
      desc: 'Drag-and-drop canvas, project explorer, and property panels for rapid screen design.',
    },
    {
      img: componentConfig,
      title: 'Component Configuration',
      desc: 'Bind components to MQTT tags, data-source queries, and AI-generated expressions.',
    },
    {
      img: runtimeMenu,
      title: 'Runtime Navigation',
      desc: 'Role-based access to dashboards, reports, alarms, and AI chat for operators.',
    },
    {
      img: runtimeDashboard,
      title: 'Live Runtime Dashboard',
      desc: 'Real-time process visualization running in any modern browser.',
    },
  ];

  const industries = [
    { icon: faIndustry, name: 'Manufacturing' },
    { icon: faSun, name: 'Energy & Utilities' },
    { icon: faCookieBite, name: 'Food & Beverage' },
    { icon: faPills, name: 'Pharma' },
    { icon: faCar, name: 'Automotive' },
    { icon: faTint, name: 'Water Treatment' },
  ];

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="title" content={pageTitle} />
        <meta name="description" content={pageDescription} />
        <meta name="og:title" content={pageTitle} />
        <meta name="og:description" content={pageDescription} />
        <meta name="keywords" content={pageKeywords} />
        <meta name="og:keywords" content={pageKeywords} />
      </Helmet>

      {/* Hero */}
      <div className="preview-hero" style={heroStyle}>
        <div style={heroBgShapes}>
          <div style={{ ...shapeBase, ...shape1 }} />
          <div style={{ ...shapeBase, ...shape2 }} />
          <div style={{ ...shapeBase, ...shape3 }} />
          <div style={{ ...shapeBase, ...shape4 }} />
        </div>
        <div className="container">
          <div
            className="row align-items-center"
            style={{ position: 'relative', zIndex: 1 }}
          >
            <div
              className="col-lg-6 order-lg-last"
              data-aos="fade-left"
              data-aos-duration="1200"
            >
              <div style={heroMediaFrame}>
                <img
                  src={previewHero}
                  alt="prai.view Designer workspace showing the actual industrial dashboard design environment"
                  className="w-100"
                  style={{ borderRadius: 12, display: 'block' }}
                />
              </div>
            </div>
            <div
              className="col-lg-6 order-lg-first"
              style={{ paddingTop: '20px' }}
              data-aos="fade-right"
              data-aos-duration="1200"
            >
              {/* <div style={heroBadge}>
                <img
                  src={praiViewLogo}
                  alt="prai.view"
                  style={{
                    height: 18,
                    width: 'auto',
                    marginRight: 10,
                    display: 'block',
                  }}
                />
                Industrial AI Dashboard Platform
              </div> */}
              <h1 style={heroTitle}>
                <img
                  src={praiViewLogo}
                  alt="prai.view"
                  style={{
                    marginRight: 10,
                    display: 'block',
                  }}
                />{' '}
                Build. Deploy. Monitor.
                <br />
              </h1>
              <p style={heroSubtitle}>
                An agent-first industrial application platform that lets
                integrators design SCADA, MES, and EMS dashboards with AI
                assistance, then deploy them as secure browser-based runtimes —
                with local ML inference and read-only SCADA integration.
              </p>
              <div style={heroActions}>
                <button
                  onClick={handleQuote}
                  className="theme-btn-two"
                  style={btnPrimary}
                >
                  Request a Quote
                </button>
                <a
                  href="#features"
                  className="theme-btn-one"
                  style={btnOutline}
                >
                  Explore Features{' '}
                  <FontAwesomeIcon
                    icon={faArrowRight}
                    style={{ marginLeft: 8 }}
                  />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Intro */}
      <div
        className="fancy-text-block-thirteen pt-80 md-pt-60"
        style={{ background: '#fff' }}
      >
        <div className="container">
          <div className="row align-items-center">
            <div
              className="col-lg-12 order-lg-first"
              data-aos="fade-right"
              data-aos-duration="1200"
            >
              <div className="title-style-two">
                <span style={sectionLabel}>What is prai.view</span>
                <h2>
                  Industrial operations, reimagined with AI and agent-first
                  design.
                </h2>
                <br />
              </div>
              <p>
                <strong>prai.view</strong> is an Industrial AI Dashboard Builder
                & Runtime Platform developed by{' '}
                <strong>PI Incorporation (GUIVIEW)</strong>. It gives licensed
                integrators and plant engineers everything they need to design,
                deploy, and operate modern SCADA, MES, and EMS applications —
                without heavy client installations or risky control writes.
              </p>
              <br />
              <p>
                The platform combines a fast{' '}
                <strong>Tauri-based desktop Designer</strong>, a secure{' '}
                <strong>browser-based Runtime</strong>, a{' '}
                <strong>NestJS backend</strong>, and an{' '}
                <strong>AI orchestrator</strong> that can scaffold entire
                projects from natural language. Local TensorFlow models run
                on-premise for predictive maintenance and anomaly detection,
                while cloud LLMs handle reasoning, reporting, and code
                generation.
              </p>
              <br />
              <p>
                Every AI generation produces reviewable artifacts —
                implementation plans, screenshots, and walkthroughs — so your
                team stays in control.
              </p>
            </div>
            {/* <div
              className="col-lg-6 col-md-8 m-auto order-lg-last"
              data-aos="fade-left"
              data-aos-duration="1200"
            >
              <div
                style={{
                  position: 'relative',
                  borderRadius: 20,
                  overflow: 'hidden',
                  boxShadow: '0 20px 50px rgba(0,0,0,0.08)',
                  background: '#fff',
                  padding: 12,
                }}
              >
                <img
                  src={designerIntro}
                  alt="prai.view Designer workspace showing drag-and-drop industrial dashboard design"
                  className="w-100"
                  style={{ borderRadius: 12, display: 'block' }}
                />
                <div style={mediaCaption}>
                  AI-assisted designer with drag-and-drop canvas, property
                  panels, and agent sidebar.
                </div>
              </div>
            </div> */}
          </div>
        </div>
      </div>

      {/* Features */}
      <div
        className="fancy-feature-twentyFour pt-80 md-pt-60"
        id="features"
        style={{
          background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
        }}
      >
        <div className="container">
          <div className="text-center mb-60">
            <span style={sectionLabel}>Key Capabilities</span>
            <h2 style={{ ...sectionTitle, color: '#0f172a' }}>
              Benefits Of prai.view
            </h2>
            <p style={sectionDesc}>
              Everything you need to deliver intelligent industrial applications
              faster, safer, and at lower cost.
            </p>
          </div>
          <div className="row">
            {features.map((f, i) => (
              <div
                key={i}
                className="col-lg-4 col-md-6 d-flex mb-35"
                data-aos="fade-up"
                data-aos-duration="1200"
                data-aos-delay={(i % 3) * 100}
              >
                <div
                  className="block-style-twentyFour w-100"
                  style={featureCard}
                >
                  <div className="d-flex align-items-start">
                    <div style={featureIconWrap}>
                      <FontAwesomeIcon
                        icon={f.icon}
                        style={{ color: '#fff', fontSize: 22 }}
                      />
                    </div>
                    <div className="text">
                      <h4>{f.title}</h4>
                      <p>{f.text}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* AI & ML */}
      <div
        className="fancy-text-block-thirteen pt-80 md-pt-60"
        style={{ background: '#fff' }}
      >
        <div className="container">
          <div className="row align-items-center">
            <div
              className="col-lg-6 order-lg-first"
              data-aos="fade-right"
              data-aos-duration="1200"
            >
              <div className="title-style-two">
                <span style={sectionLabel}>AI & Machine Learning</span>
                <h2
                  style={{
                    ...sectionTitle,
                    color: '#0f172a',
                    textAlign: 'left',
                  }}
                >
                  Predict. Detect. Forecast. Locally.
                </h2>
                <br />
              </div>
              <p style={{ color: '#475569' }}>
                prai.view keeps your process data on-premise with a local
                TensorFlow engine, while cloud LLMs power generative design and
                reporting through a provider-agnostic AI manager.
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: '28px 0 0' }}>
                {aiCapabilities.map((cap, i) => (
                  <li
                    key={i}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 14,
                      marginBottom: 18,
                      fontSize: 17,
                      color: '#334155',
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faCheck}
                      style={{
                        color: '#6f55ff',
                        fontSize: 20,
                        marginTop: 4,
                        flexShrink: 0,
                      }}
                    />
                    <span>
                      <strong style={{ color: '#0f172a' }}>{cap.title}:</strong>{' '}
                      {cap.text}
                    </span>
                  </li>
                ))}
              </ul>
              <button
                onClick={handleQuote}
                className="theme-btn-two"
                style={{ marginTop: 16 }}
              >
                Talk to Our AI Team
              </button>
            </div>
            <div
              className="col-lg-6 col-md-8 m-auto order-lg-last"
              data-aos="fade-left"
              data-aos-duration="1200"
            >
              <img
                src={aiMlIllustration}
                alt="Local AI engine predicting equipment remaining useful life, detecting anomalies, and forecasting industrial tag values"
                className="w-100"
                style={{
                  maxWidth: 600,
                  display: 'block',
                  margin: '0 auto',
                  filter: 'drop-shadow(0 20px 40px rgba(16,22,33,0.1))',
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Modules */}
      <div
        className="fancy-feature-twentyFour pt-80 md-pt-60"
        id="modules"
        style={{
          background: 'linear-gradient(180deg, #f1f5f9 0%, #f8fafc 100%)',
        }}
      >
        <div className="container">
          <div className="text-center mb-60">
            <span style={sectionLabel}>Built-In Modules</span>
            <h2 style={{ ...sectionTitle, color: '#0f172a' }}>
              MES, EMS & More
            </h2>
            <p style={sectionDesc}>
              A growing library of industrial modules ready to use out of the
              box.
            </p>
          </div>
          <div className="row">
            {modules.map((m, i) => (
              <div
                key={i}
                className="col-lg-3 col-md-4 col-sm-6 mb-25"
                data-aos="fade-up"
                data-aos-duration="1200"
                data-aos-delay={(i % 4) * 60}
              >
                <div style={moduleCard}>
                  <div
                    style={{
                      ...moduleIconWrap,
                      background: i % 2 === 0 ? '#fff7ed' : '#f0edff',
                    }}
                  >
                    <FontAwesomeIcon
                      icon={m.icon}
                      style={{
                        color: i % 2 === 0 ? '#ff6b00' : '#6f55ff',
                        fontSize: 22,
                      }}
                    />
                  </div>
                  <h4
                    style={{
                      fontSize: 17,
                      fontWeight: 700,
                      color: '#0f172a',
                      marginBottom: 8,
                    }}
                  >
                    {m.title}
                  </h4>
                  <p
                    style={{
                      fontSize: 14,
                      lineHeight: 1.55,
                      color: '#64748b',
                      margin: 0,
                    }}
                  >
                    {m.text}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Showcase */}

      {/* Industries */}
      <div
        className="fancy-feature-twentyFour pt-60 pb-80 md-pt-40 md-pb-60"
        id="industries"
        style={{ background: 'linear-gradient(180deg, #f8fafc 0%, #fff 100%)' }}
      >
        <div className="container">
          <div className="text-center mb-40">
            <span style={sectionLabel}>Industries</span>
            <h2 style={{ ...sectionTitle, color: '#0f172a' }}>
              Built For Your Industry
            </h2>
            <p style={sectionDesc}>
              Flexible templates and modules tailored to common industrial
              verticals.
            </p>
          </div>
          <div className="row justify-content-center">
            {industries.map((ind, i) => (
              <div
                key={i}
                className="col-6 col-md-4 col-lg-2 mb-20"
                data-aos="fade-up"
                data-aos-duration="1200"
                data-aos-delay={i * 50}
              >
                <div style={industryItem}>
                  <FontAwesomeIcon
                    icon={ind.icon}
                    style={{ color: '#ff6b00', fontSize: 28, marginBottom: 12 }}
                  />
                  <h4
                    style={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: '#0f172a',
                      margin: 0,
                    }}
                  >
                    {ind.name}
                  </h4>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA */}
      {/* <div className="container pb-100 md-pb-80">
        <div style={ctaBanner} data-aos="zoom-in" data-aos-duration="1200">
          <div style={ctaShape1} />
          <div style={ctaShape2} />
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div className="row align-items-center">
              <div className="col-lg-8">
                <img
                  src={praiViewLogo}
                  alt="prai.view"
                  style={{
                    height: 24,
                    width: 'auto',
                    marginBottom: 16,
                    display: 'block',
                    filter: 'brightness(0) invert(1)',
                  }}
                />
                <h2
                  style={{
                    color: '#fff',
                    fontSize: 'clamp(28px, 4vw, 38px)',
                    fontWeight: 700,
                    marginBottom: 12,
                  }}
                >
                  Ready to build smarter industrial dashboards?
                </h2>
                <p
                  style={{
                    color: 'rgba(255,255,255,0.75)',
                    fontSize: 17,
                    maxWidth: 560,
                    margin: 0,
                  }}
                >
                  Talk to our team about prai.view licensing, pilot programs,
                  and how AI-assisted design can accelerate your SCADA, MES, and
                  EMS projects.
                </p>
              </div>
              <div className="col-lg-4 text-lg-end mt-30 mt-lg-0">
                <button
                  onClick={handleQuote}
                  className="theme-btn-two"
                  style={{
                    background: '#ff6b00',
                    color: '#fff',
                    borderColor: '#ff6b00',
                  }}
                >
                  <FontAwesomeIcon
                    icon={faQuoteRight}
                    style={{ marginRight: 8 }}
                  />
                  Request a Quote
                </button>
              </div>
            </div>
          </div>
        </div>
      </div> */}
    </>
  );
};

/* Inline styles */
const heroStyle = {
  padding: '160px 0 90px',
  position: 'relative',
  overflow: 'hidden',
  background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
};

const heroBgShapes = {
  position: 'absolute',
  inset: 0,
  pointerEvents: 'none',
  zIndex: 0,
};

const shapeBase = {
  position: 'absolute',
  borderRadius: '50%',
};

const shape1 = {
  width: 420,
  height: 420,
  background: 'radial-gradient(circle, rgba(111,85,255,0.14), transparent 70%)',
  top: -120,
  right: -80,
};

const shape2 = {
  width: 300,
  height: 300,
  background: 'radial-gradient(circle, rgba(255,107,0,0.12), transparent 70%)',
  bottom: '10%',
  left: -80,
};

const shape3 = {
  width: 12,
  height: 12,
  background: '#ffb840',
  top: '25%',
  right: '12%',
};

const shape4 = {
  width: 8,
  height: 8,
  background: '#fd6a5e',
  bottom: '30%',
  right: '25%',
};

const heroBadge = {
  display: 'inline-flex',
  alignItems: 'center',
  background: 'rgba(111,85,255,0.1)',
  color: '#6f55ff',
  padding: '8px 16px',
  borderRadius: 50,
  fontSize: 14,
  fontWeight: 600,
  marginBottom: 22,
};

const heroTitle = {
  fontSize: 'clamp(42px, 5vw, 68px)',
  fontWeight: 700,
  lineHeight: 1.08,
  color: '#0f172a',
  marginBottom: 24,
  letterSpacing: -1.5,
};

const gradientText = {
  background: 'linear-gradient(135deg, #3bb0d7 0%, #6f55ff 50%, #ff6b00 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
};

const heroSubtitle = {
  fontSize: 18,
  lineHeight: 1.7,
  color: '#475569',
  marginBottom: 34,
};

const heroActions = {
  display: 'flex',
  gap: 16,
  flexWrap: 'wrap',
};

const heroMediaFrame = {
  background: '#fff',
  borderRadius: 18,
  padding: 12,
  boxShadow: '0 30px 60px rgba(16,22,33,0.12)',
  border: '1px solid #e2e8f0',
  maxWidth: 620,
  margin: '0 auto',
};

const btnPrimary = {
  background: '#ff6b00',
  color: '#fff',
  boxShadow: '0 8px 24px rgba(255,107,0,0.28)',
  border: 'none',
};

const btnOutline = {
  background: 'transparent',
  color: '#6f55ff',
  border: '2px solid #6f55ff',
};

const sectionLabel = {
  display: 'inline-block',
  fontSize: 14,
  fontWeight: 700,
  letterSpacing: 2,
  textTransform: 'uppercase',
  color: '#ff6b00',
  marginBottom: 14,
};

const sectionTitle = {
  fontSize: 'clamp(32px, 4vw, 42px)',
  fontWeight: 700,
  lineHeight: 1.2,
  marginBottom: 18,
  textAlign: 'center',
};

const sectionDesc = {
  fontSize: 17,
  color: '#64748b',
  maxWidth: 760,
  margin: '0 auto',
  textAlign: 'center',
};

const mediaCaption = {
  position: 'absolute',
  bottom: 24,
  left: 24,
  right: 24,
  background: 'rgba(15,23,42,0.85)',
  color: '#fff',
  padding: '12px 18px',
  borderRadius: 10,
  fontSize: 14,
  backdropFilter: 'blur(6px)',
};

const featureCard = {
  background: '#fff',
  borderRadius: 18,
  padding: '28px 24px',
  boxShadow: '0 10px 40px rgba(0,0,0,0.06)',
  transition: 'all 0.3s ease',
  border: '1px solid #f1f5f9',
  height: '100%',
};

const featureIconWrap = {
  background: 'linear-gradient(135deg, #6f55ff 0%, #3bb0d7 100%)',
  borderRadius: 14,
  minWidth: 54,
  height: 54,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: 18,
};

const archCard = {
  background: '#fff',
  borderRadius: 20,
  padding: '38px 28px',
  boxShadow: '0 10px 40px rgba(0,0,0,0.06)',
  textAlign: 'center',
  transition: 'all 0.3s ease',
  height: '100%',
};

const moduleCard = {
  background: '#fff',
  borderRadius: 14,
  padding: '26px 20px',
  boxShadow: '0 6px 24px rgba(0,0,0,0.05)',
  textAlign: 'center',
  transition: 'all 0.3s ease',
  border: '1px solid #f1f5f9',
  height: '100%',
};

const moduleIconWrap = {
  width: 54,
  height: 54,
  borderRadius: 14,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto 16px',
};

const showcaseCard = {
  background: '#fff',
  borderRadius: 18,
  overflow: 'hidden',
  boxShadow: '0 10px 40px rgba(0,0,0,0.07)',
  transition: 'all 0.3s ease',
  border: '1px solid #f1f5f9',
};

const showcaseCaption = {
  padding: '22px 24px',
};

const industryItem = {
  background: '#fff',
  borderRadius: 14,
  padding: '24px 12px',
  textAlign: 'center',
  boxShadow: '0 6px 24px rgba(0,0,0,0.05)',
  transition: 'all 0.3s ease',
  border: '1px solid #f1f5f9',
  height: '100%',
};

const ctaBanner = {
  background: 'linear-gradient(135deg, #101621 0%, #1a2332 100%)',
  borderRadius: 24,
  padding: '60px 50px',
  position: 'relative',
  overflow: 'hidden',
};

const ctaShape1 = {
  position: 'absolute',
  width: 300,
  height: 300,
  borderRadius: '50%',
  background: '#6f55ff',
  opacity: 0.15,
  top: -100,
  right: -50,
};

const ctaShape2 = {
  position: 'absolute',
  width: 200,
  height: 200,
  borderRadius: '50%',
  background: '#ff6b00',
  opacity: 0.15,
  bottom: -60,
  left: '10%',
};

export default PreviewPage;
